import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    fetchRetailElectricProviders,
    newAgreement
} from '../../../api/api';
import { useMyStore } from '../../../common/store/AppStoreProvider';
import type { Provider, SmtGetInfoProps } from '../../../types';
import { CheckboxItem } from '../../checkbox-item';
import { FormLoading } from '../../form-loading';
import { Button } from '../../styles/Button.styled';

export const SmtGetInfo: React.FC<SmtGetInfoProps> = ({ handleShowAgreement, handleBack, hideBack }) => {
    const { t } = useTranslation('usage');
    const [providers, setProviders] = useState<Provider[]>([]);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [validated, setValidated] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<Provider | undefined>(undefined);
    const [showProviderError, setShowProviderError] = useState(false);
    const [showConsentError, setShowConsentError] = useState(false);
    const [meterNumber, setMeterNumber] = useState('');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isAgree, setIsAgree] = useState(false);
    const [forwardEmail, setForwardEmail] = useState(false);

    const { getAddress } = useMyStore()

    useEffect(() => {
        setLoadingProviders(true)
        fetchRetailElectricProviders().then(res => {
            if (res) setProviders(res)
            setLoadingProviders(false)
        })
    }, [])

    const handleSubmit = (evt: React.FormEvent) => {
        evt.preventDefault();
        evt.stopPropagation();

        const esiid = getAddress().esiid;
        const form = evt.currentTarget as HTMLInputElement;
        const validProvider = handleValidProvider()
        setValidated(true);
        if (!isAgree) {
            setShowConsentError(true);
            return;
        }
        if (!form.checkValidity() || !validProvider || !selectedProvider || !esiid || submitting) return;

        setSubmitting(true);
        newAgreement(
            esiid,
            meterNumber,
            selectedProvider.certificate_number,
            email,
            forwardEmail ? '1' : '0'
        ).then(res => {
            if (res && res.response && res.response.consent_id) {
                handleShowAgreement(res.response.consent_id, email);
            }
            else {
                if (res.error_code && res.error_code === '500') {
                    toast.error(`${t('Incorrect ESIID-METER-REP Combination')}`);
                }
                setSubmitting(false);
                console.error('error: new agreement');
            }
        })
    }

    const handleValidProvider = () => {
        let isValid = true
        setShowProviderError(false)
        if (!selectedProvider) {
            isValid = false
            setShowProviderError(true)
        }
        return isValid
    }

    const selectStyles = {
        control: (style: object) => ({
            ...style,
            borderColor: showProviderError ? '#dc3545 !important' : '#ced4da',
            borderRadius: '.3rem'
        }),
        singleValue: (style: object) => ({
            ...style,
            color: '#495057',
            fontSize: '1.25rem',
            lineHeight: 1.5
        }),
        valueContainer: (style: object) => ({
            ...style,
            padding: '7px 1rem'
        })
    }

    return (
        <>
            <div className='d-flex justify-content-end'>
                <img src="/images/X.svg" alt="" style={{ cursor: 'pointer' }} onClick={() => handleBack()} />
            </div>
            <div className="smt-modal position-relative d-flex flex-column align-items-center">
                <div style={{ fontSize: 30, color: '#fcc068', fontWeight: 600, marginBottom: 20 }}>{t('We are almost there!')}</div>
                <p style={{ fontSize: 20 }}>{t('To ensure the accuracy of the data, we will need your Smart Meter Texas (SMT) number and the permission to access the data')}</p>
                <Form noValidate validated={validated} onSubmit={handleSubmit} >
                    <Form.Group className='d-flex flex-column gap-3 mt-3 mb-3'>
                        <Select
                            value={selectedProvider}
                            placeholder='Provider'
                            onChange={(obj: any) => {
                                setSelectedProvider(obj)
                                setShowProviderError(false)
                            }}
                            options={providers}
                            getOptionLabel={option => `${option.business_name}`}
                            getOptionValue={option => `${option.business_name}`}
                            isSearchable={true}
                            maxMenuHeight={200}
                            styles={selectStyles}
                            isLoading={loadingProviders}
                            isDisabled={submitting}
                            className='select-control'
                        />

                        <Form.Control
                            required
                            size="lg"
                            onChange={evt => setMeterNumber(evt.target.value)}
                            value={meterNumber}
                            disabled={submitting}
                            placeholder='Meter Number'
                        />

                        <Form.Control
                            required
                            size="lg"
                            type="email"
                            onChange={evt => setEmail(evt.target.value)}
                            value={email}
                            disabled={submitting}
                            placeholder='Email'
                        />
                    </Form.Group>
                    <div className="checkbox-row">
                        <CheckboxItem
                            label={t("Would you like to receive a copy of the consent?")}
                            value="1"
                            key="0"
                            checked={forwardEmail}
                            handleClick={() => {
                                setForwardEmail(!forwardEmail)
                            }}
                            disabled={submitting}
                        />
                    </div>
                    <div className="checkbox-row">
                        <CheckboxItem
                            label={t("By clicking on the box, I agree to grant GreenUpTX permission to access my account on Smart Meter Texas (SMT). In addition, GreenUpTX shall provide all SMT email communications received by GreenUpTX on your behalf to your email address provided.")}
                            value="1"
                            key="0"
                            checked={isAgree}
                            handleClick={() => {
                                setShowConsentError(false);
                                setIsAgree(!isAgree)
                            }}
                            disabled={submitting}
                            error={showConsentError}
                        />
                    </div>
                    <Form.Group className="mb-0 d-flex justify-content-between mt-5">
                        {
                            !hideBack &&
                            <Button type="button" variant="secondary" disabled={submitting} className="me-4" onClick={() => { handleBack() }}>
                                {t('Back')}
                            </Button>
                        }
                        <Button type="submit" disabled={submitting} data-test-id="cta">{t('Continue')}</Button>
                    </Form.Group>
                </Form>
                <FormLoading show={submitting} />
            </div>
        </>
    )
}
